import { useEffect, useState } from "react";
import { PrintEntry } from "../models/printEntry";
import { PrintGift } from "../models/printGift";
import agent from "../api/agent";
import { toast } from "react-toastify";
import { Autocomplete, FormControlLabel, Radio, RadioGroup, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { basename, env } from "../utils/env";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { QRCodeSVG } from "qrcode.react";

const generateReceivingCode = () => {
  const letters = "ABCDEFGHIJKLMNPQRSTUVWXYZ";
  const first = letters[Math.floor(Math.random() * letters.length)];
  const digits = Math.floor(1000 + Math.random() * 9000); // Four digits
  return `${first}${digits}`;
};

export default function PrintGiftSelect() {
  const [step, setStep] = useState(0);
  const [printEntries, setPrintEntries] = useState<PrintEntry[]>([]);
  const [printGifts, setPrintGifts] = useState<PrintGift[]>([]);

  const [selectedEntry, setSelectedEntry] = useState<PrintEntry | null>(null);
  const [selectedGiftId, setSelectedGiftId] = useState<string | null>(null);
  const [selectedDesign, setSelectedDesign] = useState<number | null>(null);

  const [errors, setErrors] = useState<any>({});
  const [receivingCode, setReceivingCode] = useState<string>("");

  useEffect(() => {
    agent.PrintEntries.list().then(setPrintEntries);
    agent.PrintEntries.listPrintGifts().then(setPrintGifts);
  }, []);

  useEffect(() => {
    let currentIndex = 0;

    let lightbox: any = new PhotoSwipeLightbox({
      gallery: "#photo-gallery",
      children: "a",
      pswpModule: () => import("photoswipe"),
    });

    lightbox.on('change', () => {
      currentIndex = lightbox.pswp?.currIndex || 0;
    });

    lightbox.on('close', () => {
      setSelectedDesign(currentIndex + 1);
    });

    lightbox.init();

    return () => {
      lightbox.destroy();
      lightbox = null;
    };
  }, []);

  const nextStep = () => setStep((prev) => Math.min(prev + 1, 2));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 0));
  const resetAll = () => window.location.reload();

  const designImages = Array.from({ length: 6 }, (_, i) => `${process.env.PUBLIC_URL}/assets/design/design-${i + 1}.png`);

  const validateForm = () => {
    let tempErrors: any = {};

    if (!selectedEntry?.name) tempErrors.name = "Ad və soyad sahəsi tələb olunur";
    if (!selectedGiftId) tempErrors.printGiftId = "Hədiyyə seçimi tələb olunur";
    if (!selectedDesign) tempErrors.design = "Dizayn seçimi tələb olunur";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>)  => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Zəhmət olmasa geri qayıdın və qırmızı ilə işarələnmiş tələb olunan sahələri doldurun!");
      return;
    }

    if (selectedEntry?.hasPicked) {
      toast.error("Siz artıq seçim etmisiniz");
      return;
    }

    const selectedGift = printGifts.find((g) => g.id === selectedGiftId);
    if (selectedGift?.stock === 0) {
      toast.error("Bu hədiyyə artıq tükənib. Zəhmət olmasa başqa birini seçin.");
      return;
    }

    const code = generateReceivingCode();
    setReceivingCode(code);

    if (selectedEntry && selectedDesign && selectedGiftId) {
      const printEntryData = {
        id: selectedEntry.id,
        name: selectedEntry?.name || "",
        datePicked: new Date().toISOString(),
        design: selectedDesign,
        receivingCode: code,
        hasPicked: true,
        editType: 1,
        printGiftId: selectedGiftId,
      }

      try {
        await agent.PrintEntries.update(printEntryData);
        setPrintEntries([...printEntries, printEntryData]);
        toast.success("Baskı hediye girdisi başarıyla eklendi");
      } catch (error) {
        toast.error("Baskı hediye girdileri eklenirken bir hata oluştu");
        return;
      }
    } else {
      toast.error("İstifadəçi seçimi xətası səbəbindən formanın doldurulması alınmadı");
      return;
    }

    nextStep()
  }

  return (
    <>
      <main
        lang="az"
        className="print-gift-screen"
        style={{
          background: `url(${process.env.PUBLIC_URL}/assets/birbank-logo.png) 28px 20px no-repeat, url(${process.env.PUBLIC_URL}/assets/baskitasarim-bg.jpg) center center no-repeat`,
          backgroundSize: '400px 61px, cover'
        }}
      >
        <div
          className="wizard-container"
          style={{transform: `translateX(-${step * 100}vw)`,}}
        >
          {/* Step 0: Welcome */}
          <section className="wizard-step first">
            <h1>Öz hədiyyəni dizayn etməyə hazırsan?</h1>
            <button className="start-button" onClick={nextStep}>
              Elə isə başla
            </button>
          </section>

          {/* Step 1: Gift & Design Selection Form */}
          <section className="wizard-step bottom">
            <form onSubmit={handleSubmit}>
              <Grid
                container
                spacing={6}
                marginBottom={2}
                sx={{ justifyContent: "space-between" }}
              >
                <Grid size={{xs: 12, sm: 3, md: 3}} sx={{textAlign: "right"}}>
                  <div className="label-grid">
                    <label>Ad Soyad</label>
                    <div className="error-container">
                      {errors.name && <small className="error">{errors.name}</small>}
                    </div>
                  </div>
                </Grid>
                <Grid size={{xs: 12, sm: 9, md: 9}} sx={{textAlign: "left"}}>
                  <Autocomplete
                    options={printEntries.filter((entry) => !entry.hasPicked)}
                    getOptionLabel={(option) => option.name}
                    value={selectedEntry}
                    onChange={(event, newValue) => {
                      setSelectedEntry(newValue);

                      if (newValue?.name) {
                        setErrors((prev: any) => ({ ...prev, name: "" }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        className="autocomplete-field"
                        error={Boolean(errors.name)}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />                  
                </Grid>                
              </Grid>

              <Grid
                container
                spacing={6}
                marginBottom={2}
                sx={{ justifyContent: "space-between" }}
              >
                <Grid size={{xs: 12, sm: 3, md: 3}} sx={{textAlign: "right"}}>
                  <div className="label-grid">
                    <label>Hədiyyəni Seç</label>
                    <small>Yalnız bir hədiyyə seçəbilərsiniz</small>
                    <div className="error-container">
                      {errors.printGiftId && <small className="error">{errors.printGiftId}</small>}
                    </div>
                  </div>
                </Grid>
                <Grid size={{xs: 12, sm: 9, md: 9}} sx={{textAlign: "left"}}>
                  <RadioGroup
                    row
                    value={selectedGiftId}
                    onChange={(e) => setSelectedGiftId(e.target.value)}
                    sx={{display: "flex", gap: "10px"}}
                  >
                    {printGifts.map((gift) => (
                      <FormControlLabel
                        key={gift.id}
                        value={gift.id}
                        control={<Radio disabled={gift.stock === 0} />}
                        disabled={gift.stock === 0}
                        label={
                          <div className={`gift-radio ${selectedGiftId === gift.id ? "selected" : ""} ${gift.stock === 0 ? "disabled" : ""}`}>
                            <img src={env + gift.giftImage} alt={gift.name} className="gift-image" />
                            <div>{gift.name}</div>
                            {gift.stock === 0 && <small className="stock-message">Stokda yoxdur</small>}
                          </div>
                        }
                      />
                    ))}
                  </RadioGroup>
                </Grid>                
              </Grid>

              <Grid
                container
                spacing={6}
                marginBottom={0}
                sx={{ justifyContent: "space-between" }}
              >
                <Grid size={{xs: 12, sm: 3, md: 3}} sx={{textAlign: "right"}}>
                  <div className="label-grid">
                    <label>Dizaynı Seç</label>
                    <small>Dizaynı böyüdə bilərsiniz</small>
                    <div className="error-container">
                      {errors.design && <small className="error">{errors.design}</small>}
                    </div>
                  </div>
                </Grid>
                <Grid id="photo-gallery" size={{xs: 12, sm: 9, md: 9}} sx={{textAlign: "left"}}>
                  <RadioGroup
                    row
                    value={selectedDesign}
                    onChange={(e) => setSelectedDesign(parseInt(e.target.value))}
                    sx={{display: "flex", gap: "10px"}}
                  >
                    {designImages.map((src, index) => (
                      <div className="design-box" key={index}>
                        <FormControlLabel
                          key={index}
                          value={index + 1}
                          control={<Radio />}
                          label={
                            <div className={`design-radio ${selectedDesign === index + 1 ? "selected" : ""}`}>
                              <img src={src} alt={`Design ${index + 1}`} className="design-image" />
                            </div>
                          }
                        />
                        <a
                          href={src}
                          rel="noreferrer"
                          data-pswp-width={540}
                          data-pswp-height={400}
                        ><ZoomInIcon className="design-icon" /></a>
                      </div>
                    ))}
                  </RadioGroup>
                </Grid>                
              </Grid>
              
              <Grid
                container
                spacing={8}
                marginBottom={0}
                sx={{ justifyContent: "space-between" }}
              >
                <Grid size={{xs: 12, sm: 12, md: 12}} sx={{textAlign: "center"}}>
                  <button className="submit-button" type="submit">Tamamla</button>
                </Grid>
              </Grid>
            </form>
          </section>

          {/* Step 2: Success */}
          <section className="wizard-step bottom">
            <h3>Təşəkkürlər</h3>
            <p className="success-message">Zəhmət olmasa kodunuzu itirməyin</p>
            <h1 className="code">{receivingCode}</h1>
            <Grid
              container
              spacing={6}
              marginBottom={0}
              sx={{ justifyContent: "space-between", alignItems: "center", width: '100%' }}
            >
              <Grid size={{xs: 12, sm: 6, md: 6}} sx={{textAlign: "right"}}>
                <h4>Kodu endirin</h4>
              </Grid>
              <Grid size={{xs: 12, sm: 6, md: 6}} sx={{textAlign: "left", margin: "10px 0"}}>
                <QRCodeSVG
                  value={`https://hediyecekilisyazilimi.com/app/qrCodeScreen/${selectedEntry?.id}`}
                  size={90}
                  fgColor="#ffffff"
                  bgColor="transparent"
                />
              </Grid>
            </Grid>
                <small>Zəhmət olmasa ekranı skrin edərdiniz</small>
            <button className="start-button" onClick={resetAll}>Başa qayıt</button>
          </section>
        </div>
      </main>
    </>
  );
}